import React, { useEffect, useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Chip,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import useApiData from "../../services/useApiData.js";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { BUYER_VIEW_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import {
    catalogDataGridSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
} from "./catalogGridStyles.js";
import AuthenticatedAssetImage from "../Helpers/AuthenticatedAssetImage.jsx";
import "./catalog.css";

function moneyText(value) {
    if (value == null || value === "") return "";
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function statusNames(value) {
    if (!Array.isArray(value)) return [];
    return value
        .map((entry) => (entry && typeof entry === "object" ? entry.name || "" : ""))
        .filter(Boolean);
}

function heroAssetFromProduct(product) {
    const photos = Array.isArray(product?.heroPhoto) ? product.heroPhoto : [];
    return (
        photos.find((asset) => asset?.gcsObject || asset?.sourceUrl) || null
    );
}

function MoneyCell({ value }) {
    const text = moneyText(value);
    if (!text) {
        return (
            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                —
            </Typography>
        );
    }
    return text;
}

function HeroCell({ asset, alt }) {
    return (
        <AuthenticatedAssetImage
            asset={asset}
            lazy
            alt={alt || ""}
            sx={{
                width: 48,
                height: 48,
                borderRadius: "8px",
                border: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
            }}
        />
    );
}

function StatusChecksCell({ names }) {
    if (!names?.length) {
        return (
            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                —
            </Typography>
        );
    }
    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                py: 0.5,
                overflow: "hidden",
            }}
        >
            {names.map((name) => (
                <Tooltip key={name} title={name} enterDelay={300}>
                    <Chip
                        size="small"
                        label={name}
                        sx={{
                            height: 24,
                            maxWidth: 180,
                            fontWeight: 600,
                            fontSize: 12,
                            borderRadius: "8px",
                            backgroundColor: "#e0f2fe",
                            color: "#075985",
                            "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            },
                        }}
                    />
                </Tooltip>
            ))}
        </Box>
    );
}

export default function BuyerViewGrid() {
    const [searchInput, setSearchInput] = useState("");
    const [searchSku, setSearchSku] = useState("");
    const [rows, setRows] = useState([]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchSku(searchInput.trim());
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const { apply: applyFilter, filterActive, barProps: filterBar } =
        useGridFilter(BUYER_VIEW_FILTER_FIELDS);

    const listUrl = useMemo(() => {
        if (!searchSku) return "";
        const params = new URLSearchParams({
            page: "1",
            pageSize: "500",
            sku: searchSku,
            includeAssets: "1",
        });
        return `api/broylesllc/products?${params.toString()}`;
    }, [searchSku]);

    const { data, error, load } = useApiData(listUrl, { enabled: Boolean(listUrl) });

    useEffect(() => {
        if (!searchSku) {
            setRows([]);
            return;
        }
        const items = data?.items || [];
        setRows(
            items.map((p) => ({
                id: p._id,
                heroAsset: heroAssetFromProduct(p),
                sku: p.listingField || p.internalSkuId || "",
                itemName: p.itemName || p.listingField || "",
                vistaMaxPrice: p.vistaMaxPrice,
                macMaxPrice: p.macMaxPrice,
                bidftaMaxPrice: p.bidftaMaxPrice,
                triangleAndersonPrice: p.triangleAndersonPrice,
                triangleRaleighPrice: p.triangleRaleighPrice,
                statusChecks: statusNames(p.statusChecks),
            }))
        );
    }, [data, searchSku]);

    const filteredRows = useMemo(() => applyFilter(rows), [rows, applyFilter]);

    const layout = useGridLayout(GRID_LAYOUT_KEYS.buyerView, {
        sortModel: [],
        columnVisibilityModel: {},
    });

    const baseColumns = useMemo(
        () => [
            {
                field: "heroAsset",
                headerName: "Hero",
                width: 72,
                sortable: false,
                filterable: false,
                hideable: false,
                renderCell: (params) => (
                    <HeroCell asset={params.row.heroAsset} alt={params.row.itemName} />
                ),
            },
            {
                field: "sku",
                headerName: "SKU",
                minWidth: 140,
                flex: 0.7,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Tooltip title={text} enterDelay={300} placement="top-start">
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                    display: "block",
                                    fontWeight: 600,
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                    fontSize: 12.5,
                                }}
                            >
                                {text}
                            </Box>
                        </Tooltip>
                    );
                },
            },
            {
                field: "itemName",
                headerName: "Product Name",
                minWidth: 240,
                flex: 1.4,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Tooltip title={text} enterDelay={300} placement="top-start">
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    width: "100%",
                                    display: "block",
                                    fontWeight: 600,
                                }}
                            >
                                {text}
                            </Box>
                        </Tooltip>
                    );
                },
            },
            {
                field: "vistaMaxPrice",
                headerName: "Vista Max Price",
                minWidth: 140,
                flex: 0.7,
                align: "right",
                headerAlign: "right",
                renderCell: (params) => <MoneyCell value={params.value} />,
            },
            {
                field: "macMaxPrice",
                headerName: "MAC Max Price",
                minWidth: 140,
                flex: 0.7,
                align: "right",
                headerAlign: "right",
                renderCell: (params) => <MoneyCell value={params.value} />,
            },
            {
                field: "bidftaMaxPrice",
                headerName: "BidFTA Max Price",
                minWidth: 150,
                flex: 0.7,
                align: "right",
                headerAlign: "right",
                renderCell: (params) => <MoneyCell value={params.value} />,
            },
            {
                field: "triangleAndersonPrice",
                headerName: "Triangle Anderson Price",
                minWidth: 180,
                flex: 0.8,
                align: "right",
                headerAlign: "right",
                renderCell: (params) => <MoneyCell value={params.value} />,
            },
            {
                field: "triangleRaleighPrice",
                headerName: "Triangle Raleigh Price",
                minWidth: 170,
                flex: 0.8,
                align: "right",
                headerAlign: "right",
                renderCell: (params) => <MoneyCell value={params.value} />,
            },
            {
                field: "statusChecks",
                headerName: "Status Checks",
                minWidth: 220,
                flex: 1.1,
                sortable: false,
                renderCell: (params) => <StatusChecksCell names={params.value} />,
            },
        ],
        []
    );

    const columns = useMemo(
        () => layout.applyWidths(baseColumns),
        [baseColumns, layout.committedWidths, layout.applyWidths]
    );

    const resultCount = filterActive ? filteredRows.length : rows.length;

    return (
        <>
            {error && (
                <Typography color="error" sx={{ mb: 1, fontSize: 13, px: 0.5 }}>
                    Failed to load buyer view.
                </Typography>
            )}
            <div className="catalog-grid-shell">
                <div className="catalog-toolbar">
                    <TextField
                        size="small"
                        placeholder="Search by SKU…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={catalogSearchFieldSx}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <span className="catalog-selection-hint">
                        {searchSku
                            ? `${resultCount} product${resultCount === 1 ? "" : "s"}`
                            : "Enter a SKU to search"}
                    </span>
                    <div className="catalog-toolbar-spacer" />
                    <GridLayoutControls layout={layout} columns={baseColumns} />
                </div>
                <GridFilterBar {...filterBar} />
                <div className="catalog-grid-main">
                    <DataGrid
                        {...appDataGridDefaultProps}
                        loading={Boolean(searchSku && load)}
                        rows={filteredRows}
                        columns={columns}
                        disableRowSelectionOnClick
                        sortModel={layout.sortModel}
                        onSortModelChange={layout.onSortModelChange}
                        columnVisibilityModel={layout.columnVisibilityModel}
                        onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                        onColumnWidthChange={layout.onColumnWidthChange}
                        paginationMode="client"
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { page: 0, pageSize: 25 } },
                        }}
                        getRowHeight={() => "auto"}
                        slotProps={appDataGridSlotProps}
                        sx={{
                            ...catalogDataGridSx,
                            "& .MuiDataGrid-cell": {
                                ...catalogDataGridSx["& .MuiDataGrid-cell"],
                                py: 1,
                                alignItems: "center",
                            },
                        }}
                        localeText={{
                            noRowsLabel: searchSku
                                ? "No products match that SKU."
                                : "Search by SKU to see buyer pricing and hero images.",
                        }}
                    />
                </div>
            </div>
        </>
    );
}
